import { createAsyncThunk, createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { decode } from '@googlemaps/polyline-codec';
import URLs from '../../../../../utils/appURLs';
import { updateToast } from '../../../../../common/redux/reducer/commonSlices/toastSlice';
import axiosInstance from '../../../../../app/config/axiosInstance';
import {
  convertEpochToDateString,
  formatDateTimeToDate
} from '../../../../../utils/commonFunctions';

type ReturnedType = any;
type ThunkArg = number;

interface Path {
  id: string;
  path: any;
  color: string;
}

interface UpdateCustomers {
  payload: any;
  pageDetails: { autoplannerID: number; pageNo: number; pageSize: number };
}

interface KhubTripState {
  isLoading: boolean;
  data: [] | null;
  path: Path[] | [];
  error: string | null;
  count: number | null;
}

// interface Trips {
//   duration: string;
//   status: string;
// }

type TripStatus = string;

const initialState: KhubTripState = {
  isLoading: false,
  data: [],
  path: [],
  error: null,
  count: 0
};

// action to get trip and order count details
export const getTripsandOrderCount = createAsyncThunk<AxiosResponse<any>, string>(
  'khubtripscount',
  async (duration, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DELIVERY_TRIPS_COUNT}duration=${duration}`
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// action to get all trip details
export const getAllTrips = createAsyncThunk<AxiosResponse<any>, any>(
  'khubtrips',
  async (data: any, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DELIVERY_TRIPS}status=${data?.status}&pageNo=${data?.pageno}&pageSize=${data?.pagesize}`,
        signal: data?.signal
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// action to get all trip details
export const getAllOptimizedRoute = createAsyncThunk<ReturnedType, ThunkArg>(
  'khubtrips/route',
  async (id, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `${URLs.DELIVERY_ROUTES}?tripId=${id}`
      });
      return response?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// action to get orders based on trips
export const getOrdersBasedTrips = createAsyncThunk<ReturnedType, ThunkArg>(
  'khubtrips/pro',
  async (id, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'GET',
        url: `delivery/list?tripId=${id}`
      });
      return response?.data?.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(error);
    }
  }
);

// // Action function to update trip
// export const autoGenerateUpdateTripsAction = createAsyncThunk<
//   ReturnedType,
//   UpdateCustomers
// >('updateautogeneratedtrips', async (data, thunkApi) => {
//   try {
//     const response = await axiosInstance({
//       method: 'PUT',
//       url: `${URLs.SCHEDULE_UPDATE}`,
//       data: data.payload
//     });
//     await thunkApi.dispatch(getAllTrips(data.pageDetails));
//     thunkApi.dispatch(
//       updateToast({
//         show: true,
//         message: response?.data?.message,
//         severity: 'success'
//       })
//     );
//     return response?.data?.data[0];
//   } catch (error: any) {
//     thunkApi.dispatch(
//       updateToast({
//         show: true,
//         message: error.response.data?.error,
//         severity: 'error'
//       })
//     );
//     return thunkApi.rejectWithValue(error);
//   }
// });

//Action for delete khub Trip
export const deleteKhubTrip = createAsyncThunk<ReturnedType, any>(
  'khub-trip/delete',
  async (data, thunkApi) => {
    try {
      const response = await axiosInstance({
        method: 'DELETE',
        url: `${URLs.DELETE_KHUB_TRIP}tripId=${data?.selectedRow?.id}`
      });
      await thunkApi.dispatch(getAllTrips(data));
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: response?.data?.message,
          severity: 'success'
        })
      );
      return response?.data?.data;
    } catch (error: any) {
      thunkApi.dispatch(
        updateToast({
          show: true,
          message: error.response.data?.error,
          severity: 'error'
        })
      );
      return thunkApi.rejectWithValue(error);
    }
  }
);

//slice for delete khub trip
const deletesliceKhubTrip = createSlice({
  name: 'khub trips delete',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(deleteKhubTrip.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteKhubTrip.fulfilled, (state, action: PayloadAction<ReturnedType>) => {
        state.isLoading = false;
        state.data = action.payload?.data;
        state.error = null;
      })
      .addCase(deleteKhubTrip.rejected, (state, action: any) => {
        state.isLoading = false;
        state.error = action.payload?.response?.data?.error || 'Something went wrong';
      });
  }
});

const tripsandOrderCountSlice = createSlice({
  name: 'khubtripsandordercount',
  initialState,
  reducers: {
    clearTrips: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getTripsandOrderCount.pending, state => {
        state.isLoading = true;
        state.data = null;
        state.error = null;
      })
      .addCase(getTripsandOrderCount.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.error = null;
      })
      .addCase(getTripsandOrderCount.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = null;
        state.error = action.error.message || 'Somthing went wrong';
      });
  }
});

const khubtripsSlice = createSlice({
  name: 'khubtripsDetails',
  initialState,
  reducers: {
    clearTrips: state => {
      state.data = [];
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getAllTrips.pending, state => {
        state.isLoading = true;
        state.data = null;
        state.error = null;
      })
      .addCase(getAllTrips.fulfilled, (state, action: any) => {
        state.isLoading = false;
        state.data = action.payload.data.listAllTripsDetailsList.map(
          (item: any, index: number) => ({
            ...item,
            overSpeedID: index + 1,
            startTimeDate: formatDateTimeToDate(
              convertEpochToDateString(item?.startTimestamp)
            ),
            driverNames: item?.tripDeliveriesInfo?.driverName
          })
        );
        state.count = action.payload.data.count;
        state.error = null;
      })
      .addCase(getAllTrips.rejected, (state, action: any) => {
        console.log(action.payload, 'action.error.name');
        if (action.error.message === 'cancelled') {
          state.isLoading = true;
        } else {
          // Normal error handling
          state.isLoading = false;
          state.data = null;
          state.error = action.error.message || 'Something went wrong';
        }
      });
  }
});

const khubtripRouteSlice = createSlice({
  name: 'khubtripsRouteDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getAllOptimizedRoute.pending, state => {
        state.isLoading = true;
        state.data = current(state).data;
        state.path = [];
        state.error = null;
      })
      .addCase(getAllOptimizedRoute.fulfilled, (state, action: any) => {
        let [optimized] = action.payload.data.optimizedRoutePolyLines;
        let [actual] = action.payload.data.traversedPathPolyline;

        let plannedPath = decode(optimized).map(([lat, lng]) => ({ lat, lng }));
        let actualPath =
          actual?.lengtth > 0 && decode(actual).map(([lat, lng]) => ({ lat, lng }));
        // let formattedDecoded = plannedPath?.map(([lat, lng]) => ({ lat, lng }));
        state.isLoading = false;
        state.data = action.payload.data;
        state.path = [
          { id: 'planned', color: '#0f53ff', path: plannedPath },
          { id: 'actual', color: 'red', path: actualPath }
        ];
        state.error = null;
      })
      .addCase(getAllOptimizedRoute.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = null;
        state.path = [];
        state.error = action.error.message || 'Somthing went wrong';
      });
  }
});

const khubtripOrdersSlice = createSlice({
  name: 'khubtripsDetails',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getOrdersBasedTrips.pending, state => {
        state.isLoading = true;
        state.data = null;
        state.error = null;
      })
      .addCase(getOrdersBasedTrips.fulfilled, (state, action: any) => {
        state.isLoading = false;
        let { deliverablesInfoList, driverName, mobileNumber } = action.payload;
        state.data = deliverablesInfoList.map((item: any, index: number) => ({
          ...item,
          overSpeedID: index + 1,
          driverName,
          mobileNumber
        }));
        state.error = null;
      })
      .addCase(getOrdersBasedTrips.rejected, (state, action: any) => {
        state.isLoading = false;
        state.data = null;
        state.error = action.error.message || 'Somthing went wrong';
      });
  }
});

export const tripsandOrderCountData = tripsandOrderCountSlice.reducer;
export const khubTripsData = khubtripsSlice.reducer;
export const khubDeleteTrip = deletesliceKhubTrip.reducer;
export const khubTripsRouteData = khubtripRouteSlice.reducer;
export const khubTripOrderData = khubtripOrdersSlice.reducer;
export const { clearTrips } = khubtripsSlice.actions;
